import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useLazyQuery, useApolloClient } from "@apollo/react-hooks";
import PropTypes from "prop-types";
import _ from "lodash";
import { useHistory, useLocation } from "react-router";

import {
    updateUrlForScrollPosition,
    getCurrentPage,
    getPaginationOffset
} from "./helpers";

const useLoadMore = ({
    query,
    queryVariables,
    pathToListKey,
    pathToTotalCountKey,
    limit,
    fetchItemsOnLoad = true,
    pageQueryParamKey = "p"
} = {}) => {
    const [currentPage, setCurrentPage] = useState(0);

    const history = useHistory();
    const location = useLocation();
    const client = useApolloClient();
    const queryString = location.search;

    const startingPageRef = useRef();

    const fetchFirstBatch = () => {
        const initialPage = getCurrentPage({ queryString, pageQueryParamKey });
        startingPageRef.current = 1;

        if (initialPage === 1) {
            fetchFirstBatchOfItems();
            return;
        }

        const pageWasCached = cacheContainsPageData({ page: initialPage });

        if (pageWasCached) {
            startingPageRef.current = initialPage;
        }

        fetchFirstBatchOfItems();
    };

    useLayoutEffect(() => {
        if (fetchItemsOnLoad) fetchFirstBatch();
    }, [fetchItemsOnLoad]);

    const startingPage = startingPageRef.current;

    useEffect(() => {
        if (currentPage > 0) {
            updateUrlForScrollPosition({
                sessionHistory: history,
                routerLocation: location,
                page: currentPage,
                pageQueryParamKey
            });
        }
    }, [currentPage]);

    const getVariables = ({ page }) => ({
        ...queryVariables,
        limit,
        offset: getPaginationOffset({ page, limit })
    });

    const cacheContainsPageData = ({ page }) => {
        /* uses try...catch because Apollo throws error
        if query is not found in cache */
        try {
            client.readQuery({
                query,
                variables: getVariables({ page })
            });
        } catch {
            return false;
        }

        return true;
    };

    const getItemsFromData = ({ data }) => _.get(data, pathToListKey);
    const getTotalItemCountFromData = ({ data }) =>
        _.get(data, pathToTotalCountKey);

    const [
        fetchFirstBatchOfItems,
        { loading, data, fetchMore, called }
    ] = useLazyQuery(query, {
        variables: getVariables({ page: startingPage }),
        onCompleted: data => {
            const fetchedItems = getItemsFromData({ data });
            if (!fetchedItems.length) return;

            setCurrentPage(startingPage);
        }
    });

    const loadMore = () => {
        const nextPage = currentPage + 1;

        fetchMore({
            variables: getVariables({ page: nextPage }),
            updateQuery: (prevResult, { fetchMoreResult }) => {
                const fetchedItems = getItemsFromData({ data });
                if (!fetchedItems.length) return prevResult;

                setCurrentPage(nextPage);

                const updatedData = updateDataWithNewPaginationItems();

                client.writeQuery({
                    query,
                    data: updatedData,
                    variables: getVariables({ page: nextPage })
                });

                return updatedData;

                function updateDataWithNewPaginationItems() {
                    const previousPaginationItems = getItemsFromData({
                        data: prevResult
                    });
                    const newPaginationItems = getItemsFromData({
                        data: fetchMoreResult
                    });
                    const updatedPaginationItems = [
                        ...previousPaginationItems,
                        ...newPaginationItems
                    ];

                    const updatedData = {
                        ...fetchMoreResult
                    };
                    _.set(updatedData, pathToListKey, updatedPaginationItems);
                    return updatedData;
                }
            }
        });
    };

    const items = getItemsFromData({ data });

    return {
        items: items || [],
        loading,
        currentPage,
        loadMore,
        totalCount: getTotalItemCountFromData({ data }),
        fetchFirstBatch,
        totalItemsLoaded: items && items.length,
        firstBatchFetched: called
    };
};

useLoadMore.propTpyes = {
    query: PropTypes.string.isRequired,
    queryVariables: PropTypes.object,
    pathToListKey: PropTypes.string.isRequired,
    limit: PropTypes.number,
    offset: PropTypes.number
};

export default useLoadMore;
